/**
 * @module app.cm
 */

import { autoinject, singleton, TaskQueue } from 'aurelia-framework';
import { ComponentBase, StandardLoadOptions, Sorting, State, SortDirection } from 'app/shared/lib/essette-site';
import { UserService } from 'app/core/services/user-service';
import { TaskService, ITaskSearchFilters } from 'app/core/services/task-service';
import { allActionsSucceeded, apiCall } from 'app/shared/lib/essette-api';
import { ITasksConfig, IQueuesConfig } from 'app/core/models/essette-interfaces';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { QueueService } from 'app/core/services/queue-service';
import { EssTableCustomElement, SelectEvent } from 'app/shared/elements/ess-table';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { ICollapse } from 'app/shared/attributes/ess-collapse-attributes';
import { IPopup } from 'app/shared/elements/popup-base';
import { openCaseSummary } from 'app/cm/components/summary/case-summary';
import { CaseService } from 'app/cm/services/case-service';
import { I18N } from 'aurelia-i18n';
import { guid } from 'app/shared/lib/guid';
import { EventAggregator } from 'aurelia-event-aggregator';
import { ReturnFocusEvent, SetFocusEvent } from 'app/shared/lib/events';
import { Toaster } from 'app/shared/lib/toaster';
import { isNullOrUndefined } from 'app/shared/lib/utilities';
import { confirmation, ConfirmationOptions, IConfirmationDialogResult } from 'app/shared/elements/ess-confirm';
import { requiredFields as memberRequiredFields } from 'app/cd/elements/members/member-display';
import { ITranslation } from 'app/shared/lib/i18n';
import * as Core from 'app/core/models/essette-api';
import * as CM from 'app/cm/models/essette-api';
import * as CD from 'app/cd/models/essette-api';

interface TaskWithDelegatedToName extends CM.Task {
    delegatedToName?: string | null;
}

@singleton()
@autoinject()
export class TasksInterventionsPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected taskResults: Array<TaskWithDelegatedToName> = [];
    protected searchFilters: ITaskSearchFilters | null = null;
    protected tempSearchFilters: ITaskSearchFilters | null = null;
    protected delegateMode: boolean = false;

    protected delegatedToUser: Core.User | null = null;
    protected delegatedToQueue: CM.CaseQueue | null = null;
    protected selectedDelegatedToQueue: CM.CaseQueue | null = null;
    protected queueUsers: Array<Core.User> = [];
    protected delegateToQueueUsers: Array<Core.User> | null = null;
    protected queueProviders: Array<CD.Provider> | null = null;
    protected delegateToState: State = new State();

    protected selectedTask: Core.Task | null = null;
    protected selectedTasks: CM.Task[] = [];
    protected selectedTaskId: string | null = null;
    protected loadModal: boolean = false;
    protected taskOrInterventions: string = '';
    protected selectAtLeastOne: string = '';
    protected selectedSingular: string = '';
    protected selectedPlural: string = '';

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 20;

    protected hasSearchAll: boolean = false;
    protected hasCaseOwner: boolean = false;
    protected hasCareTeam: boolean = false;
    protected hasCaseRoute: boolean = false;
    private hasTaskRoute: boolean = false;
    private hasInterventionRoute: boolean = false;
    protected showCaseSummaryLink: boolean = false;
    protected openCaseSummary = openCaseSummary;

    protected id: guid = guid();

    //lookup objects
    private taskTypes: ReadonlyArray<CM.TaskTargetType> | null = null;
    private caseQueues: ReadonlyArray<CM.CaseQueue> = [];
    protected searchQueues: ReadonlyArray<CM.TaskQueue> = [];
    protected caseOwners: ReadonlyArray<CM.CaseOwner> = [];

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $table!: EssTableCustomElement<CM.Task>;
    protected $collapsible!: ICollapse;
    protected $floatingPanelCollapsible!: ICollapse;
    protected $searchCriteriaHeader!: HTMLElement;
    protected $modal!: IPopup;
    protected $currentCriteriaHeader!: HTMLElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly events: EventAggregator,
        private readonly userService: UserService,
        private readonly taskService: TaskService,
        private readonly queueService: QueueService,
        private readonly caseService: CaseService,
        private readonly queue: TaskQueue,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {

        super(...rest);
        this.selectAtLeastOne = this.i18n.tr('ui:messages.info.selectAtLeastOne', { what: `${this.i18n.tr('core:task.$').toLowerCase()}${this.i18n.tr('ui:lists.conjunctions.or')}${this.i18n.tr('cm:carePlanIntervention.$').toLowerCase()}`});
        this.refreshTexts();

        const tasksConfig: ITasksConfig = {
            serviceRootUrl: 'cm'
        };

        const queuesConfig: IQueuesConfig = {
            serviceRootUrl: 'cm/tasks'
        };

        this.taskService.configure(tasksConfig);
        this.queueService.configure(queuesConfig);
    }

    protected async canActivate(): Promise<boolean> {
        await this.checkPermissions();

        return true;
    }

    protected async activate(): Promise<void> {
        await this.loadLookups();

        this.delegateMode = false;

        // Only load provider list if at least one queue is a provider delegate type
        if(this.searchQueues.some((q) => q.delegated?.type?.code === 'CD.Provider')) {
            this.loadQueueProviders();
        }
        if(!this.searchFilters || !this.tempSearchFilters) {
            this.setDefaultFilters();
        }
        this.search();
    }

    ////****************** Events ***************************************************////
    protected async queueChanged(newQueue: Core.Queue | null): Promise<void> {
        if(this.tempSearchFilters) {

            if(!newQueue) {
                this.tempSearchFilters.delegatedToUser = null;
                this.tempSearchFilters.delegatedToProvider = null;
                return;
            }

            if(newQueue?.delegated?.type?.code === 'CD.Provider') {
                this.tempSearchFilters.delegatedToUser = null;
            } else {
                this.tempSearchFilters.delegatedToProvider = null;
                if(newQueue?.id) await this.loadQueueUsers(newQueue.id);
            }

            // Clear current delegatedTo selection if not in the new queue users collection
            if(this.tempSearchFilters?.delegatedToUser && !this.queueUsers.some(user => user.id === this.tempSearchFilters!.delegatedToUser!.id) && !this.tempSearchFilters?.delegatedToMe) {
                this.tempSearchFilters.delegatedToUser = null;
            }
        }
    }

    protected delegateToQueueChanged(newQueue: Core.Queue | null): void {
        if(newQueue?.id) {
            if(this.selectedDelegatedToQueue && newQueue.id !== this.delegatedToQueue?.id) {
                this.selectedDelegatedToQueue = null;
                this.delegatedToQueue = null;
                this.delegatedToUser = null;
            };
            this.selectedDelegatedToQueue = newQueue;
            this.delegatedToQueue = newQueue;
            this.loadDelegateToQueueUsers(newQueue.id);

        } else {
            this.delegateToQueueUsers = [];
            this.delegatedToUser = null;
        };
    }

    protected handleEnableBulkAction(): void {
        if(this.delegateMode) {
            this.$floatingPanelCollapsible.open(0);
        }

        this.clearSelected();
    }

    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting } = this;

        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        // only refresh results if table already has records
        if(this.resultCount > 0) await this.search();
    }

    protected handleSubmit(): void {
        if(!this.state.saving && !this.state.loading) {
            if (this.tempSearchFilters) {
                if(isNullOrUndefined(this.tempSearchFilters.dueDateEnd)){
                    this.tempSearchFilters.dueDateEnd = this.tempSearchFilters.dueDateStart;
                }
            }
            this.search(true); // true to trigger a new search, clearing selected checkboxes
        }
    }

    protected handleKeyDown(e: KeyboardEvent): boolean {
        if(e.keyCode === 13 && e.ctrlKey) {
            this.handleSubmit();
            return false;
        }
        return true;
    }

    protected selectChanged({ detail }: SelectEvent<CM.Task>): void {
        // if item is missing - a select all event occurred
        if(!detail.item) {
            // unselect all items
            if(detail.items.length === 0) {
                this.taskResults.forEach(task => {
                    const index = this.selectedTasks.findIndex(
                        selected => selected.id === task.id
                    );
                    if(index !== -1) {
                        this.selectedTasks.splice(index, 1);
                    }
                });
            } else {
                // save all items
                detail.items.forEach(item => {
                    const alreadySelected = this.selectedTasks.some(
                        task => task.id === item.id
                    );
                    if(!alreadySelected) {
                        this.selectedTasks.push(item);
                    }
                });
            }
        } else {
            // save selected checkboxes to restore later logic
            const match = detail.items.some(
                item => item.id === detail.item?.id
            );
            // Check for matching id (allows dups and id's correct task)
            if(match) {
                // add to selectedTasks if not already present
                const alreadySelected = this.selectedTasks.some(
                    task => task.id === detail.item?.id
                );
                if(!alreadySelected) {
                    this.selectedTasks.push(detail.item!);
                }
            } else {
                // remove from selectedTasks if it exists based on id
                const index = this.selectedTasks.findIndex(
                    task => task.id === detail.item?.id
                );
                if(index !== -1) {
                    this.selectedTasks.splice(index, 1);
                }
            }
        }
        this.refreshTexts();
    }

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasSearchAll = this.accountContext.hasPermission('CM.Task.Query.SearchAll');
        this.hasCaseOwner = this.accountContext.hasPermission('CM.Case.Duty.Owner');
        this.hasCareTeam = this.accountContext.hasRouteAccess('cm.case.careteam');
        this.hasCaseRoute = this.accountContext.hasRouteAccess('cm.case');
        this.hasTaskRoute = this.accountContext.hasRouteAccess('cm.task');
        this.hasInterventionRoute = this.accountContext.hasRouteAccess('cm.intervention');
        this.showCaseSummaryLink = this.accountContext.hasRouteAccess('cm.case.summary');
    }

    private restoreSelected() {
        if(!(this.selectedTasks && this.selectedTasks.length > 0)) return;
        // Give aurelia a chance to bind data to the table before we ask the table to restore selected items
        this.queue.queueTask(() => {
            this.$table.restoreSelected(this.selectedTasks);
        });
    }

    protected clearSelected(): void {
        this.selectedTasks.splice(0);
        this.$table.deselectAll();
        this.delegatedToQueue = null;
        this.selectedDelegatedToQueue = null;
        this.delegatedToUser = null;
    }

    protected refreshTexts() {
        this.selectedSingular = this.i18n.tr('ui:messages.info.selected', { what:  this.i18n.tr('ui:pagination.record.$').toLowerCase(), number: this.selectedTasks.length });
        this.selectedPlural = this.i18n.tr('ui:messages.info.selected', { what: this.i18n.tr('ui:pagination.record.$_plural').toLowerCase(), number: this.selectedTasks.length });
    }

    private setDefaultFilters(): void {
        // Default binding for my cases/delegated to user

        //if we're null we need to initialize here
        this.searchFilters = {};
        this.tempSearchFilters = {};

        if(this.hasCaseOwner) {
            this.searchFilters.assignedTo = { id: this.env.user.id, name: { display: this.env.user.description } };
        } else {
            this.searchFilters.delegatedToUser = { id: this.env.user.id, $display: this.env.user.description, description: this.env.user.description };
        }

        if(this.hasTaskRoute && !this.hasInterventionRoute) {
            const type = this.taskTypes?.find((t) => t.code === 'CM.Case');
            this.searchFilters.type = type;
        }
        else if(this.hasInterventionRoute && !this.hasTaskRoute) {
            const type = this.taskTypes?.find((t) => t.code === 'CM.CarePlanGoal');
            this.searchFilters.type = type;
        }

        this.searchFilters.isOpen = true;
        this.tempSearchFilters = { ...this.searchFilters };
    }

    public showAvailableCriteria(): void {
        if(!this.hasSearchAll || !this.$collapsible) return;
        this.$collapsible.open(0);
        this.$collapsible.close(1);
    }

    public showSelectedCriteria(): void {
        if(!this.hasSearchAll || !this.$collapsible) return;

        this.$collapsible.close(0);
        this.$collapsible.open(1);
    }

    public clear(returnFocus?: boolean): void {
        this.searchFilters = {};
        this.tempSearchFilters = {};
        this.clearResults();
        this.clearSelected();
        this.$collapsible.open(0);
        if(returnFocus) this.events.publish(new SetFocusEvent(this.$searchCriteriaHeader));
    }

    protected clearResults(): void {
        this.taskResults = [];
        if(this.$pager) this.$pager.updatePage(0);
        this.resultCount = 0;
    }

    protected toggleMyCases(value: boolean): void {
        if(!this.searchFilters) this.searchFilters = {};
        if(!this.tempSearchFilters) this.tempSearchFilters = {};

        this.tempSearchFilters.myCases = value;

        this.tempSearchFilters.assignedTo = value ? { id: this.env.user.id, $display: this.env.user.description } : undefined;
    }

    protected toggleDelegatedToMe(value: boolean): void {
        if(!this.searchFilters) this.searchFilters = {};
        if(!this.tempSearchFilters) this.tempSearchFilters = {};

        this.tempSearchFilters.delegatedToMe = value;

        this.tempSearchFilters.delegatedToUser = value ? { id: this.env.user.id, $display: this.env.user.description } : undefined;
        this.tempSearchFilters.delegatedToProvider = null;
    }

    protected toggleMyCareTeam(value: boolean): void {
        if(!this.searchFilters) this.searchFilters = {};
        if(!this.tempSearchFilters) this.tempSearchFilters = {};

        this.tempSearchFilters.myCareTeam = value;

        this.tempSearchFilters.careTeamParticipant = value ? { id: this.env.user.id, $display: this.env.user.description } : undefined;
    }

    protected openQueueHistory(selected: Core.Task): void {
        this.selectedTask = selected;
        this.selectedTaskId = selected.id!;
        this.loadModal = true;
        this.$modal.open();
    }

    protected getTaskLabel(task: CM.Task): string {
        if(!!task?.description) return task.description;

        // if there is no description, default to Task/Intervention for the label
        switch(task?.attachedTo?.type?.code) {
            case 'CM.CarePlanGoal':
                return 'cm:carePlanIntervention.$';
            case 'CM.Case':
                return 'core:task';
            default:
                return 'core:task';
        }
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadLookups(): Promise<void> {
        try {
            this.state.beginLoad();

            const options: StandardLoadOptions = { all: true };

            const [taskTypes, caseOwners, queues, users] = await Promise.all(
                [
                    this.caseService.loadTaskTargetTypes(options, undefined, ['code']),
                    this.caseService.loadCaseOwners(options, undefined, undefined, undefined, ['name.display']),
                    this.queueService.loadQueues(null, options, undefined, ['delegated.type.code', 'description']),
                    this.userService.loadUsers(options, undefined, undefined, ['description'])
                ]
            );

            this.taskTypes = taskTypes.results;
            this.caseOwners = caseOwners.results;
            this.searchQueues = queues.results;
            this.caseQueues = queues.results.filter(q => q.delegated?.type?.code === 'Core.User'); //as opposed to Provider
            this.queueUsers = users.results;

            for(const item of this.taskTypes) {
                if(item.code === 'CM.Case') item.$display = this.i18n.tr('cm:task.$');
                if(item.code === 'CM.CarePlanGoal') item.$display = this.i18n.tr('cm:carePlanIntervention.$');
            }
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    public async search(newSearch: boolean = false): Promise<void> {
        if(newSearch) {
            this.clearSelected();
            this.searchFilters = { ...this.tempSearchFilters };
        }

        const options: StandardLoadOptions = {
            sorting: this.sorting,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            includeInactive: false
        };

        const includes = [
            'case',
            'case.assignedTo',
            'delegated.queue',
            'delegated.toProvider',
            'delegated.toUser',
            'member'
        ];

        const fields = [
            'attachedTo.type.code',
            'case.assignedTo.description',
            'case.isCheckedOut',
            'case.number',
            'delegated.queue.description',
            'delegated.toProvider.description',
            'delegated.toUser.description',
            'description',
            'due.date',
            'isCompleted',
            'member.eligibility.isCurrent',
            'metadata.completed.timestamp',
            'metadata.dueDateTimeSet.timestamp',
        ];

        memberRequiredFields.forEach(field => !fields.includes(`member.${field}`) ? fields.push(`member.${field}`) : null);

        try {
            this.state.beginLoad();
            const response = await this.taskService.search(this.searchFilters || {}, options, includes, fields);

            this.taskResults = response?.results || [];
            this.taskResults.forEach(task => {
                task.delegatedToName = task.delegated?.toProvider?.description || task.delegated?.toUser?.description;
            });
            this.resultCount = response?.count || 0;
            this.showSelectedCriteria();
            if(!newSearch) this.restoreSelected();
            this.events.publish(new SetFocusEvent(this.$currentCriteriaHeader));
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
            this.events.publish(new ReturnFocusEvent());
        }
    }

    @apiCall
    private async loadQueueUsers(queueId: string): Promise<void> {
        if(!!this.queueUsers) this.queueUsers.splice(0);

        try {
            this.delegateToState.beginLoad();
            const response = await this.queueService.loadQueueUsers(queueId, { all: true }, undefined, ['description']);
            this.queueUsers = response.results;

        } catch(error) {
            throw error;
        } finally {
            this.delegateToState.endLoad();
        }
    }

    @apiCall
    private async loadDelegateToQueueUsers(queueId: string): Promise<void> {
        if(!!this.delegateToQueueUsers) this.delegateToQueueUsers.splice(0);

        try {
            this.delegateToState.beginLoad();
            const response = await this.queueService.loadQueueUsers(queueId, { all: true }, undefined, ['description']);
            this.delegateToQueueUsers = response.results;

            // Clear current delegatedToUser selection if not in the new queue users collection
            if(this.delegatedToUser && !this.delegateToQueueUsers.some(user => user.id === this.delegatedToUser!.id)) {
                this.delegatedToUser = null;
            }
        } catch(error) {
            throw error;
        } finally {
            this.delegateToState.endLoad();
        }
    }

    @apiCall
    private async loadQueueProviders(): Promise<void> {
        try {
            this.delegateToState.beginLoad();
            const response = await this.queueService.loadPortalProviders('', { all: true }, undefined, ['description']);
            this.queueProviders = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.delegateToState.endLoad();
        }
    }

    @apiCall
    protected async delegateSave(): Promise<void> {
        if(this.selectedTasks.length < 1) return;

        let result: IConfirmationDialogResult = {
            reason: '',
            dialogResponse: ConfirmationOptions.OK
        };

        const isSingle = this.selectedTasks.length === 1;
        const messageWhat = isSingle ? this.getTaskInterventionLabel().toLowerCase() : this.getTaskInterventionLabel(true).toLowerCase();
        const message: string = this.i18n.tr('ui:messages.actions.updateSelected', { what: messageWhat });
        const singleTaskDelegatedToUser = this.i18n.tr('ui:actions.delegate.record.toUser.$', { who: this.delegatedToUser?.$display , what: this.delegatedToQueue?.description });
        const multipleTasksDelegatedToUser = this.i18n.tr('ui:actions.delegate.record.toUser.$_plural', { number: this.selectedTasks.length, who: this.delegatedToUser?.$display , what: this.delegatedToQueue?.description });
        const singleTaskNoUser = this.i18n.tr('ui:actions.delegate.record.$', { what: this.delegatedToQueue?.description });
        const multipleTasksNoUser = this.i18n.tr('ui:actions.delegate.record.$_plural', { number: this.selectedTasks.length, what: this.delegatedToQueue?.description });

        let subMessage: string;

        if (isSingle) {
            if (this.delegatedToUser) {
                subMessage = singleTaskDelegatedToUser;
            } else {
                subMessage = singleTaskNoUser;
            };
         } else {
            if (this.delegatedToUser) {
                subMessage = multipleTasksDelegatedToUser;
            } else {
                subMessage = multipleTasksNoUser;
            };
        };
        result = await confirmation.showCustom(message, subMessage, confirmation.options.StandardConfirm, false);
        if(!(result.dialogResponse === confirmation.options.OK)) return;

        const savedTasks: Core.Task[] = this.selectedTasks.map(task => {
            return {
                id: task.id,
                delegated: {
                    queue: this.delegatedToQueue ? { id: this.delegatedToQueue.id } : null,
                    to: this.delegatedToUser ? {
                        id: this.delegatedToUser.id,
                        type: { code: 'Core.User' }
                    } : null
                }
            } as Core.Task;
        });

        try {
            this.state.beginSave();

            const progressToast = this.toaster.standard.batch('core:task.description.$long', savedTasks.length);
            progressToast.show();

            const responses = await this.taskService.updateTasksBatch(savedTasks, progressToast);
            const success = allActionsSucceeded(responses);

            const count = savedTasks.length;
            const plural = count > 1;
            if(success){

                const message: ITranslation = {
                    key: plural ? 'ui:messages.events.delegated_plural': 'ui:messages.events.delegated',
                    count: count,
                    what: plural ? 'core:task.description.$long_plural' : 'core:task.description.$long'
                };
                this.toaster.info().message(message).show();
            } else {
                const message: ITranslation = {
                    key: plural ? 'ui:messages.events.delegatedFailed_plural' : 'ui:messages.events.delegatedFailed',
                    count: count,
                    what: plural ? 'core:task.description.$long_plural' : 'core:task.description.$long'
                };
                this.toaster.error().message(message).show();
            }

            this.clearSelected();
            this.search();
        } catch(error) {
            throw error;
        } finally {
            this.state.endSave();
        }
    }

    private getTaskInterventionLabel(multiple: boolean = false, full?: boolean): string {
        if(full) {
            return this.i18n.tr(`core:task.description.$full`).toLowerCase();
        } else {
            const pluralIndicator = multiple ? '$_plural' : "$";
            return `${this.i18n.tr(`core:task.${pluralIndicator}`)}/${this.i18n.tr(`cm:carePlanIntervention.${pluralIndicator}`)}`
        }
    }
}
